import React from 'react'
import type { ConversationProcessingResultEventData } from '@super/shared-types'
import {
  formatUSPhoneForDisplay,
  normalizePhoneNumber,
  formatPropertiesAndGroupsDisplay,
} from '@super/shared-types'
import {
  Building2Icon,
  CheckCircleIcon,
  FileTextIcon,
  ListCheckIcon,
  SmileIcon,
  UserIcon,
} from 'lucide-react'
import { MessageActionsMenu } from '../MessageActionsMenu'
import { ScratchpadCard } from './ScratchpadCard'

interface ConversationProcessingSummaryMessageProps {
  data: ConversationProcessingResultEventData
  threadId?: string
  messageId?: string
  companyId?: string
  sessionId?: string
  onSessionDetailsClick?: (sessionId: string) => void
}

export const ConversationProcessingSummaryMessage: React.FC<
  ConversationProcessingSummaryMessageProps
> = ({
  data,
  threadId,
  messageId,
  companyId,
  sessionId,
  onSessionDetailsClick,
}) => {
  const { userSummary } = data

  // Handle case where userSummary or its critical fields are missing
  if (!userSummary?.summary || !userSummary?.contactDetails) {
    return (
      <div className="mb-4 px-6 max-w-[520px]">
        <div className="border bg-gray-50 dark:bg-muted border-gray-200 dark:border-border rounded-lg p-4">
          <div className="text-center">
            <h3 className="font-semibold text-gray-900 dark:text-foreground text-sm mb-2">
              Conversation Processing Complete
            </h3>
            <p className="text-xs text-gray-600 dark:text-muted-foreground">
              Summary not available
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Safe destructuring with defaults
  const {
    title = 'Summary',
    summary,
    contactDetails,
    isResolved = false,
    nextSteps,
    sentiment,
  } = userSummary

  // Build contact details string 'Name Lastname (###)###-#### (Email)'
  const contactDetailsParts: string[] = []
  if (contactDetails?.name) {
    contactDetailsParts.push(contactDetails.name)
  } else {
    contactDetailsParts.push('Unknown Contact')
  }
  if (userSummary.contactDetails.phone) {
    // Normalize phone from OpenAI's 10-digit format to E.164 before formatting
    const normalizedPhone =
      normalizePhoneNumber(userSummary.contactDetails.phone) ||
      userSummary.contactDetails.phone
    contactDetailsParts.push(formatUSPhoneForDisplay(normalizedPhone))
  }
  // Only show email if it's actually available (not "not available")
  if (contactDetails?.email && contactDetails.email !== 'not available') {
    contactDetailsParts.push(`(${contactDetails.email})`)
  }
  const contactDetailsText = contactDetailsParts.join(' ')

  return (
    <div className="mb-4 px-6 max-w-[600px] mx-auto group relative">
      <div className="border bg-gray-50 dark:bg-muted border-gray-200 dark:border-border rounded-lg p-4 relative group/card">
        {threadId && messageId && (
          <div className="absolute -right-10 top-2 z-30">
            <MessageActionsMenu
              threadId={threadId}
              messageId={messageId}
              companyId={companyId}
              sessionId={sessionId}
              onSessionDetailsClick={onSessionDetailsClick}
              tooltipSide="right"
            />
          </div>
        )}
        {/* Title */}
        <div className="mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-foreground text-sm">
            {title}
          </h3>
        </div>

        {/* Conversation Summary */}
        <div className="mb-3">
          <div className="flex items-center mb-1">
            <span className="w-4 h-4 inline-flex items-center">
              <FileTextIcon className="w-3" />
            </span>
            <span className="text-xs text-gray-600 dark:text-muted-foreground font-medium ml-1">
              Conversation Summary:
            </span>
          </div>
          <div className="text-xs font-medium text-gray-900 dark:text-foreground leading-relaxed ml-5">
            {summary}
          </div>
        </div>

        {/* Contact Details */}
        <div className="mb-3">
          <div className="flex items-center mb-1">
            <span className="w-4 h-4 inline-flex items-center">
              <UserIcon className="w-3" />
            </span>
            <span className="text-xs text-gray-600 dark:text-muted-foreground font-medium ml-1">
              Contact details:
            </span>
          </div>
          <div className="text-xs font-medium text-gray-900 dark:text-foreground ml-5">
            {contactDetailsText}
          </div>
        </div>

        {/* Properties & Groups */}
        <div className="mb-3">
          <div className="flex items-center mb-1">
            <span className="w-4 h-4 inline-flex items-center">
              <Building2Icon className="w-3" />
            </span>
            <span className="text-xs text-gray-600 dark:text-muted-foreground font-medium ml-1">
              Properties & Groups:
            </span>
          </div>
          <div className="text-xs font-medium text-gray-900 dark:text-foreground ml-5">
            {formatPropertiesAndGroupsDisplay(
              data.propertyMatches,
              data.contactGroupMatches,
            )}
          </div>
        </div>

        {/* Action Required */}
        {nextSteps && (
          <div className="mb-3">
            <div className="flex items-center mb-1">
              <span className="w-4 h-4 inline-flex items-center">
                <ListCheckIcon className="w-3" />
              </span>
              <span className="text-xs text-gray-600 dark:text-muted-foreground font-medium ml-1">
                Action required:
              </span>
            </div>
            <div className="text-xs font-medium text-gray-900 dark:text-foreground ml-5">
              {nextSteps}
            </div>
          </div>
        )}

        {/* Resolved by AI */}
        <div className="mb-2 flex items-center">
          <span className="w-4 h-4 inline-flex items-center">
            <CheckCircleIcon className="w-3" />
          </span>
          <span className="text-xs text-gray-600 dark:text-muted-foreground font-medium ml-1">
            Resolved by AI agent:
          </span>
          <span className="text-xs font-medium text-gray-900 dark:text-foreground">
            {isResolved ? 'Yes' : 'No'}
          </span>
        </div>

        {/* Sentiment */}
        {sentiment && (
          <div className="mb-2 flex items-center">
            <span className="w-4 h-4 inline-flex items-center">
              <SmileIcon className="w-3" />
            </span>
            <span className="text-xs text-gray-600 dark:text-muted-foreground font-medium ml-1">
              Conversation sentiment:
            </span>
            <span className="text-xs font-medium text-gray-900 dark:text-foreground ml-1">
              {sentiment}
            </span>
          </div>
        )}
      </div>

      {sessionId && <ScratchpadCard sessionId={sessionId} />}
    </div>
  )
}
